import React, { useEffect, useState, useMemo, useCallback } from 'react'
import { Field, useFormikContext } from 'formik'
import { useTranslate } from 'react-admin'
import { Link, makeStyles } from '@material-ui/core'
import Alert from '@material-ui/lab/Alert'
import PropTypes from 'prop-types'
import { useQuery } from 'react-query'
import YouTubeIcon from '@material-ui/icons/YouTube'
import LiveTvIcon from '@material-ui/icons/LiveTv'
import MyTextField from '../form/fields/MyTextField'
import GAME_EVENT_FORM_BROADCAST_FIELDS from '../../utils/constants/gameEventFormBroadcastFields'
import { isFormFieldDisabled } from '../../utils/helpers/isFieldDisabled'
import CROPPER_SHAPES from '../../utils/constants/cropperShapes'
import CourtLogosPreviewEditBox from '../CourtLogosPreviewEditBox/CourtLogosPreviewEditBox'
import ImageCropWithEdit from '../ui/ImageCropComponents/ImageCropWithEdit/ImageCropWithEdit'
import MyAutocomplete from '../form/fields/MyAutocomplete'
import QueryKeys from '../../utils/constants/queryKeys'
import getPrivacyStatus from '../../Apis/event/getPrivacyStatus'
import StreamField from './StreamFields/StreamField'
import localStorageHelper from '../../utils/helpers/localStorageHelper'
import localStorageIds from '../../utils/constants/localStorageIds'
import { youtubeNeedsReauthorization } from '../../utils/helpers/youtubeLink'
import youtubeStreamFieldsStyles from './youtubeStreamFields.styles'

const YoutubeStreamFields = (props) => {
  const { state, broadcastId, allowOrgYoutube } = props
  const { values, setFieldValue } = useFormikContext()
  const [youtubeConfig, setYoutubeConfig] = useState(
    values[GAME_EVENT_FORM_BROADCAST_FIELDS.EVENT_YT_CONFIG],
  )
  const [streamKey, setStreamKey] = useState(
    values[GAME_EVENT_FORM_BROADCAST_FIELDS.STREAM_DESTINATION],
  )
  // true → YouTube channel tab, false → Stream key URI tab.
  const [useOrgsYoutube, setUseOrgsYoutube] = useState(
    !!broadcastId || !values[GAME_EVENT_FORM_BROADCAST_FIELDS.STREAM_DESTINATION],
  )
  const translate = useTranslate()

  const useStyles = useMemo(() => makeStyles(youtubeStreamFieldsStyles), [])
  const classes = useStyles()

  const isFormDisabled = useMemo(() => isFormFieldDisabled(state), [state])

  // The org still has a channel on file, but Google refused the refresh token — so the YouTube
  // destination is withheld for new broadcasts and the reason has to be said out loud.
  const { localStorageValue: selectedOrg } = localStorageHelper(localStorageIds.SELECTED_ORG)
  const needsReconnect = !allowOrgYoutube && youtubeNeedsReauthorization(selectedOrg)

  // TODO: Uplift the useOrgsYoutube state in formik, so the values ytConfig values and streamKey are nulled in parse and not here (will remove states, useEffects and make fields null)
  useEffect(() => {
    if (
      values[GAME_EVENT_FORM_BROADCAST_FIELDS.EVENT_YT_CONFIG] !== null &&
      useOrgsYoutube === true
    ) {
      setYoutubeConfig(values[GAME_EVENT_FORM_BROADCAST_FIELDS.EVENT_YT_CONFIG])
    }
  }, [values[GAME_EVENT_FORM_BROADCAST_FIELDS.EVENT_YT_CONFIG], useOrgsYoutube])

  useEffect(() => {
    if (
      values[GAME_EVENT_FORM_BROADCAST_FIELDS.STREAM_DESTINATION] !== null &&
      useOrgsYoutube === false
    ) {
      setStreamKey(values[GAME_EVENT_FORM_BROADCAST_FIELDS.STREAM_DESTINATION])
    }
  }, [values[GAME_EVENT_FORM_BROADCAST_FIELDS.STREAM_DESTINATION], useOrgsYoutube])

  // Reflect external destination changes (e.g. Reuse Setup) by switching to the matching tab:
  // a stream key → Custom RTMP, a YouTube config → YouTube channel.
  useEffect(() => {
    if (values[GAME_EVENT_FORM_BROADCAST_FIELDS.STREAM_DESTINATION]) {
      setUseOrgsYoutube(false)
    } else if (values[GAME_EVENT_FORM_BROADCAST_FIELDS.EVENT_YT_CONFIG]) {
      setUseOrgsYoutube(true)
    }
  }, [
    values[GAME_EVENT_FORM_BROADCAST_FIELDS.STREAM_DESTINATION],
    values[GAME_EVENT_FORM_BROADCAST_FIELDS.EVENT_YT_CONFIG],
  ])

  const { data: privacyStatuses } = useQuery([QueryKeys.GET_PRIVACY_STATUS], () =>
    getPrivacyStatus(),
  )

  const thumbnailPath = useMemo(
    () =>
      `${GAME_EVENT_FORM_BROADCAST_FIELDS.EVENT_YT_CONFIG}[${GAME_EVENT_FORM_BROADCAST_FIELDS.YOUTUBE_THUMBNAIL}]`,
    [],
  )
  const titlePath = useMemo(
    () =>
      `${GAME_EVENT_FORM_BROADCAST_FIELDS.EVENT_YT_CONFIG}[${GAME_EVENT_FORM_BROADCAST_FIELDS.BROADCAST_TITLE}]`,
    [],
  )
  const visibilityPath = useMemo(
    () =>
      `${GAME_EVENT_FORM_BROADCAST_FIELDS.EVENT_YT_CONFIG}[${GAME_EVENT_FORM_BROADCAST_FIELDS.YOUTUBE_VISIBILITY}]`,
    [],
  )

  const setCroppedImage = useCallback((name, value) => setFieldValue(name, value), [setFieldValue])

  // Switching destination nulls the field belonging to the other destination and restores the
  // one for the newly-selected destination, so submit only carries the active destination's data
  // (previously handled via the Collapse onEnter/onExited lifecycle of the radio group).
  const selectYoutube = useCallback(() => {
    if (useOrgsYoutube) return
    setUseOrgsYoutube(true)
    setFieldValue(GAME_EVENT_FORM_BROADCAST_FIELDS.STREAM_DESTINATION, null)
    setFieldValue(GAME_EVENT_FORM_BROADCAST_FIELDS.EVENT_YT_CONFIG, {
      broadcastId,
      ...youtubeConfig,
    })
  }, [useOrgsYoutube, setFieldValue, broadcastId, youtubeConfig])

  const selectStreamKey = useCallback(() => {
    if (!useOrgsYoutube) return
    setUseOrgsYoutube(false)
    setFieldValue(GAME_EVENT_FORM_BROADCAST_FIELDS.EVENT_YT_CONFIG, null)
    setFieldValue(GAME_EVENT_FORM_BROADCAST_FIELDS.STREAM_DESTINATION, streamKey)
  }, [useOrgsYoutube, setFieldValue, streamKey])

  const onVisibilityChange = useCallback(
    (e, setVisibility) => {
      const selectedValue = e.target.textContent
      setVisibility(visibilityPath, selectedValue)
    },
    [visibilityPath],
  )

  const onTabKeyDown = (handler) => (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      handler()
    }
  }

  // When youtube is not linked, there is no destination choice — just the stream key field.
  // A linked-but-expired channel lands here too; say why the choice disappeared instead of
  // letting the tab quietly vanish. The Integrations link opens in a new tab so following it
  // doesn't throw away a half-filled event form.
  if (!allowOrgYoutube) {
    return (
      <>
        {needsReconnect && (
          <Alert variant='outlined' severity='warning' className={classes.reconnectAlert}>
            {translate('ra.text.youtubeReconnectRequiredForBroadcast')}{' '}
            <Link
              href={`/apps${window.location.search}`}
              target='_blank'
              rel='noopener noreferrer'
            >
              {translate('ra.text.reconnectOnIntegrations')}
            </Link>
          </Alert>
        )}
        <StreamField state={state} />
      </>
    )
  }

  return (
    <div>
      <div className={classes.destTabs}>
        <div
          role='button'
          tabIndex={0}
          onClick={selectYoutube}
          onKeyDown={onTabKeyDown(selectYoutube)}
          className={`${classes.destTab} ${useOrgsYoutube ? classes.destTabActive : ''}`}
        >
          <YouTubeIcon className={classes.destTabIcon} />
          <span>{translate('ra.text.youtubeChannelTab')}</span>
        </div>
        <div
          role='button'
          tabIndex={0}
          onClick={selectStreamKey}
          onKeyDown={onTabKeyDown(selectStreamKey)}
          className={`${classes.destTab} ${!useOrgsYoutube ? classes.destTabActive : ''}`}
        >
          <LiveTvIcon className={classes.destTabIcon} />
          <span>{translate('ra.text.streamKeyTab')}</span>
        </div>
      </div>

      {useOrgsYoutube ? (
        <div className={classes.ytFields}>
          <div className={classes.ytTitleRow}>
            <ImageCropWithEdit
              defaultImageSrc={
                values[GAME_EVENT_FORM_BROADCAST_FIELDS.EVENT_YT_CONFIG]?.[
                  GAME_EVENT_FORM_BROADCAST_FIELDS.YOUTUBE_THUMBNAIL
                ] ?? ''
              }
              setCroppedImage={(croppedImage) => setCroppedImage(thumbnailPath, croppedImage)}
              aspect={16 / 9}
              minZoom={0.2}
              cropShape={CROPPER_SHAPES.RECT}
              dialogBottomText={translate('ra.text.thumbnailEdit')}
              isDisabled={isFormDisabled}
              editIconSize='18px'
              EditPreviewComponent={CourtLogosPreviewEditBox}
              editPreviewComponentClassName={classes.compactThumbnail}
            />
            <MyTextField
              name={titlePath}
              label={translate('ra.formFields.youtubeVideoTitle')}
              required={useOrgsYoutube}
              disabled={isFormDisabled}
              fullWidth
            />
          </div>
          <Field
            name={visibilityPath}
            label={translate('ra.formFields.youtubeVisibility')}
            fullWidth
            variant='filled'
            options={privacyStatuses?.map((status) => status.displayName) || []}
            component={MyAutocomplete}
            onChange={(e) => onVisibilityChange(e, setFieldValue)}
            value={
              values[GAME_EVENT_FORM_BROADCAST_FIELDS.EVENT_YT_CONFIG]?.[
                GAME_EVENT_FORM_BROADCAST_FIELDS.YOUTUBE_VISIBILITY
              ]
            }
            disabled={isFormDisabled}
            autoSelect={false}
            required={useOrgsYoutube}
          />
        </div>
      ) : (
        <StreamField state={state} />
      )}
    </div>
  )
}

YoutubeStreamFields.propTypes = {
  state: PropTypes.string,
  broadcastId: PropTypes.string,
  allowOrgYoutube: PropTypes.bool,
}

export default YoutubeStreamFields
